import { MessageToast } from "scripts/ui/components/toast";
import { ERR_NETWORK } from "scripts/ui/components/errors";

export class TableImporter {
    private fileInput: HTMLInputElement;
    private modal: HTMLElement | null;
    private statusDiv: HTMLElement | null;
    private confirmBtn: HTMLElement | null;
    private cancelBtn: HTMLElement | null;
    private downloadBtn: HTMLElement | null;
    
    constructor() {
        this.fileInput = this.createFileInput();
        this.modal = document.getElementById('importTableModal');
        this.statusDiv = document.getElementById('importTableStatus');
        this.confirmBtn = document.getElementById('importTableConfirmBtn');
        this.cancelBtn = document.getElementById('importTableCancelBtn');
        this.downloadBtn = document.getElementById('importTableDownloadExampleBtn');
        
        this.init();
    }
    
    private createFileInput(): HTMLInputElement {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.txt';
        input.style.display = 'none';
        document.body.appendChild(input);
        return input;
    }
    
    private init(): void {
        this.initConfirmButton();
        this.initCancelButton();
        this.initDownloadButton();
    }
    
    private initConfirmButton(): void {
        if (!this.confirmBtn) return;
        
        const newBtn = this.confirmBtn.cloneNode(true);
        this.confirmBtn.parentNode?.replaceChild(newBtn, this.confirmBtn);
        this.confirmBtn = newBtn as HTMLElement;
        
        this.confirmBtn.addEventListener('click', () => this.handleConfirm());
    }
    
    private initCancelButton(): void {
        if (!this.cancelBtn) return;
        
        const newBtn = this.cancelBtn.cloneNode(true);
        this.cancelBtn.parentNode?.replaceChild(newBtn, this.cancelBtn);
        this.cancelBtn = newBtn as HTMLElement;
        
        this.cancelBtn.addEventListener('click', () => this.hideModal());
    }
    
    private initDownloadButton(): void {
        if (!this.downloadBtn) return;
        
        const newBtn = this.downloadBtn.cloneNode(true);
        this.downloadBtn.parentNode?.replaceChild(newBtn, this.downloadBtn);
        this.downloadBtn = newBtn as HTMLElement;
        
        this.downloadBtn.addEventListener('click', () => this.downloadExample());
    }
    
    private async handleConfirm(): Promise<void> {
        const file = this.getSelectedFile();
        if (!file) {
            this.showStatus('Выберите файл!', 'error');
            return;
        }
        
        await this.validateAndSave(file);
    }
    
    private getSelectedFile(): File | null {
        return this.fileInput.files?.[0] || null;
    }
    
    private async validateAndSave(file: File): Promise<void> {
        this.showStatus('Проверка файла...', 'info', true);
        
        try {
            const validationResult = await this.validateFile(file);
            
            if (!validationResult.is_valid) {
                this.showValidationError(validationResult);
                return;
            }
            
            const shouldSave = confirm(validationResult.message + '\n\nСохранить файл?');
            if (!shouldSave) {
                this.showStatus('Сохранение отменено', 'info');
                setTimeout(() => this.hideModal(), 1500);
                return;
            }
            
            await this.saveFile();
            
        } catch (error) {
            this.showStatus(ERR_NETWORK, 'error');
        }
    }
    
    private async validateFile(file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch('/api/muon-table-txt/validate', {
            method: 'POST',
            body: formData
        });
        
        return await response.json();
    }
    
    private async saveFile(): Promise<void> {
        this.showStatus('Сохранение...', 'info', true);
        
        const response = await fetch('/api/muon-table-txt/save', {
            method: 'POST'
        });
        
        const result = await response.json();
        
        if (response.ok) {
            this.showStatus(result.message, 'success');
            setTimeout(() => this.hideModal(), 2000);
        } else {
            this.showStatus(result?.detail || 'Не удалось сохранить таблицу', 'error');
        }
    }
    
    private showValidationError(result: any): void {
        let errorMsg = '❌ Файл не соответствует структуре!\n';
        if (result.checks) {
            if (!result.checks.has_muon_header) errorMsg += '• Отсутствует заголовок Muon\n';
            if (!result.checks.has_standard_rock) errorMsg += '• Отсутствует "standard rock"\n';
            if (!result.checks.has_density) errorMsg += '• Отсутствует плотность\n';
            if (!result.checks.has_table_header) errorMsg += '• Отсутствует таблица\n';
        }
        this.showStatus(errorMsg, 'error');
    }
    
    private downloadExample(): void {
        window.location.href = '/api/muon-table-txt/download-example';
    }
    
    private showStatus(message: string, type: 'success' | 'error' | 'info', withProgress: boolean = false): void {
        if (!this.statusDiv) return;
        
        this.statusDiv.style.display = 'block';
        
        const bgColor = type === 'success' ? '#1a5c3a' : (type === 'error' ? '#b92525' : '#3c98ee');
        this.statusDiv.style.backgroundColor = bgColor;
        this.statusDiv.style.color = '#fff';
        this.statusDiv.style.padding = '10px';
        this.statusDiv.style.borderRadius = '8px';
        
        if (withProgress) {
            this.statusDiv.innerHTML = `<div style="display: flex; align-items: center; gap: 10px;">
                <div style="flex:0 0 auto; box-sizing:border-box; width: 20px; height: 20px; border: 2px solid #fff; border-top-color: transparent; border-radius: 50%; animation: spin 1s linear infinite;"></div>
                <span>${message}</span>
            </div>`;
        } else {
            this.statusDiv.innerHTML = message;
        }
        
        if (type !== 'error' && !withProgress) {
            setTimeout(() => {
                if (this.statusDiv) this.statusDiv.style.display = 'none';
            }, 3000);
        }
    }
    
    public showModal(): void {
        if (!this.modal) return;
        
        this.resetModal();
        this.closeSettingsModal();
        this.modal.style.display = 'flex';
        this.setupModalCloseOnBackground();
    }
    
    private resetModal(): void {
        this.fileInput.value = '';
        if (this.statusDiv) this.statusDiv.style.display = 'none';
    }
    
    private closeSettingsModal(): void {
        const settingsModal = document.getElementById('settings-modal');
        if (settingsModal && settingsModal.style.display === 'flex') {
            settingsModal.style.display = 'none';
        }
    }
    
    private setupModalCloseOnBackground(): void {
        if (!this.modal) return;
        
        this.modal.onclick = (e) => {
            if (e.target === this.modal) {
                this.hideModal();
            }
        };
    }
    
    private hideModal(): void {
        if (this.modal) {
            this.modal.style.display = 'none';
        }
    }
}